import React, { useState, useEffect } from "react";
import { SelectChangeEvent, InputAdornment } from "@mui/material";
import {
  ArcStack,
  ArcText,
  ArcOutlinedSelect,
  ArcMenuItem,
  ArcOutlinedInput,
  ArcPagination,
} from "@src/core/ui";
import {
  OrderStatusEnum,
  OrderStatusNames,
  OrderEntity,
  OrderMissionEntity,
} from "@src/features/order/entities";
import { useDispatch, useSelector } from "react-redux";
import { useGetOrdersByStatusQuery } from "@src/features/order/redux/api";
import { setMapLocation } from "@src/core/controllers";
import {
  setSelectedHistoryOrder,
  selectSelectedHistoryOrder,
} from "@src/features/history/redux";
import { ArcBaseSidebar, ArcOrderCard } from "@src/core/components";
import { isNewOrder, isMissionOrder } from "@src/features/order/utils";
import { useSearchParams } from "react-router-dom";
import { PAGINATION_DEFAULT_SIZE } from "@src/core/config";
import { ReactComponent as SearchIcon } from "@src/assets/icons/search.svg";
import { ReactComponent as CloseIcon } from "@src/assets/icons/close.svg";

const SEARCH_FETCH_SIZE = 5000;

export type HistoryFilterType =
  | OrderStatusEnum.COMPLETED
  | OrderStatusEnum.CANCELLED
  | OrderStatusEnum.ERROR;

export const HistoryPageSidebar: React.FC = () => {
  const dispatch = useDispatch();
  const selectedOrder = useSelector(selectSelectedHistoryOrder);
  const [searchParams, setSearchParams] = useSearchParams();

  const getInitialFilter = (): HistoryFilterType => {
    const statusParam = searchParams.get("status");
    if (statusParam) {
      const status = statusParam.toLowerCase() as HistoryFilterType;
      if (Object.values(OrderStatusEnum).includes(status)) {
        return status;
      }
    }
    return OrderStatusEnum.COMPLETED;
  };

  const [filter, setFilter] = useState<HistoryFilterType>(getInitialFilter);
  const [page, setPage] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchPage, setSearchPage] = useState(0);
  const filterJustChangedRef = React.useRef(false);

  useEffect(() => {
    const statusParam = searchParams.get("status");
    if (statusParam) {
      const status = statusParam.toLowerCase() as HistoryFilterType;
      if (Object.values(OrderStatusEnum).includes(status) && status !== filter) {
        setFilter(status);
        setPage(0);
      }
    } else if (filter !== OrderStatusEnum.COMPLETED) {
      setFilter(OrderStatusEnum.COMPLETED);
      setPage(0);
    }
  }, [searchParams, filter]);

  const isSearchMode = Boolean(searchQuery.trim());
  const statusParam = filter.toUpperCase();

  const { data: ordersData, isLoading, isFetching } = useGetOrdersByStatusQuery(
    {
      status: statusParam,
      page,
      size: PAGINATION_DEFAULT_SIZE,
    },
    {
      skip: isSearchMode,
      refetchOnMountOrArgChange: true,
    }
  );

  const { data: allOrdersData, isLoading: isLoadingAll, isFetching: isFetchingAll } = useGetOrdersByStatusQuery(
    {
      status: statusParam,
      page: 0,
      size: SEARCH_FETCH_SIZE,
    },
    {
      skip: !isSearchMode,
      refetchOnMountOrArgChange: true,
    }
  );

  useEffect(() => {
    return () => {
      dispatch(setSelectedHistoryOrder(null));
    };
  }, [dispatch]);

  const onSelectOrder = (
    order: OrderEntity | OrderMissionEntity,
    status: OrderStatusEnum
  ) => {
    if (isNewOrder(order)) {
      dispatch(
        setMapLocation({
          latitude: Number(order.deliveryLocation.latitude),
          longitude: Number(order.deliveryLocation.longitude),
        })
      );
      dispatch(setSelectedHistoryOrder({ ...order, status }));
    }

    if (isMissionOrder(order)) {
      dispatch(
        setMapLocation({
          latitude: Number(order.drone.latitude),
          longitude: Number(order.drone.longitude),
        })
      );
      dispatch(setSelectedHistoryOrder({ ...order, status }));
    }
  };

  const handleFilterChange = (event: SelectChangeEvent<unknown>) => {
    const newFilter = event.target.value as HistoryFilterType;
    filterJustChangedRef.current = true;
    dispatch(setSelectedHistoryOrder(null));
    setSearchParams({ status: newFilter.toUpperCase() });
    setPage(0);
    setSearchPage(0);
  };

  useEffect(() => {
    setSearchPage(0);
  }, [searchQuery]);

  const ordersFromApi: OrderEntity[] = ordersData?.content || [];
  const allOrdersFromApi: OrderEntity[] = allOrdersData?.content || [];

  const filterOrdersBySearch = React.useCallback(
    (orders: OrderEntity[], q: string) => {
      if (!q.trim()) return orders;
      const lower = q.trim().toLowerCase();
      return orders.filter((order) => {
        const o = order as OrderEntity & { orders?: OrderEntity[] };
        const idMatch = String(o.id ?? "").toLowerCase().includes(lower);
        const address =
          o.deliveryLocation?.address ??
          o.orders?.[0]?.deliveryLocation?.address ??
          "";
        const addressMatch = address.toLowerCase().includes(lower);
        return idMatch || addressMatch;
      });
    },
    []
  );

  const filteredOrders = React.useMemo(() => {
    if (!isSearchMode) return ordersFromApi;
    return filterOrdersBySearch(allOrdersFromApi, searchQuery);
  }, [isSearchMode, ordersFromApi, allOrdersFromApi, searchQuery, filterOrdersBySearch]);

  const totalFilteredCount = filteredOrders.length;
  const searchTotalPages = Math.ceil(totalFilteredCount / PAGINATION_DEFAULT_SIZE) || 0;
  const paginatedFilteredOrders = React.useMemo(() => {
    if (!isSearchMode) return filteredOrders;
    const start = searchPage * PAGINATION_DEFAULT_SIZE;
    return filteredOrders.slice(start, start + PAGINATION_DEFAULT_SIZE);
  }, [isSearchMode, filteredOrders, searchPage]);

  const displayOrders = isSearchMode ? paginatedFilteredOrders : filteredOrders;
  const totalCount = isSearchMode ? totalFilteredCount : (ordersData?.totalElements ?? 0);
  const totalPages = isSearchMode
    ? searchTotalPages
    : (ordersData?.totalPages ?? 0);
  const currentPage = isSearchMode ? searchPage : page;
  const showPagination = totalCount > 0 && totalPages > 1;
  const isLoadingList = isSearchMode ? isLoadingAll : isLoading;
  const isFetchingList = isSearchMode ? isFetchingAll : isFetching;

  const handlePageChange = React.useCallback(
    (_: unknown, newPage: number) => {
      if (isSearchMode) {
        setSearchPage(newPage - 1);
      } else {
        setPage(newPage - 1);
      }
    },
    [isSearchMode]
  );

  useEffect(() => {
    if (filterJustChangedRef.current && !isFetchingList) {
      filterJustChangedRef.current = false;
      return;
    }
    if (isLoadingList || isFetchingList || displayOrders.length === 0 || selectedOrder != null) return;
    onSelectOrder(displayOrders[0], filter);
  }, [isLoadingList, isFetchingList, displayOrders, selectedOrder, filter, onSelectOrder]);

  const filterOptions = [
    { value: OrderStatusEnum.COMPLETED, label: OrderStatusNames[OrderStatusEnum.COMPLETED] },
    { value: OrderStatusEnum.CANCELLED, label: OrderStatusNames[OrderStatusEnum.CANCELLED] },
    { value: OrderStatusEnum.ERROR, label: OrderStatusNames[OrderStatusEnum.ERROR] },
  ];

  return (
    <ArcBaseSidebar>
      <ArcStack spacing={2} marginTop={0.5} paddingBottom={1.7} sx={{ height: "100%", minHeight: 0 }}>
        <ArcOutlinedSelect
          value={filter}
          onChange={handleFilterChange}
          label="Order Type"
          sx={{ minWidth: 200 }}
        >
          {filterOptions.map((option) => (
            <ArcMenuItem key={option.value} value={option.value}>
              {option.label}
            </ArcMenuItem>
          ))}
        </ArcOutlinedSelect>

        <ArcOutlinedInput
          placeholder="Search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          fullWidth
          autoComplete="off"
          sx={{
            "& .MuiInputBase-input": { color: "white" },
            "& .MuiInputBase-input::placeholder": {
              color: "text.secondary",
              opacity: 1,
            },
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start" sx={{ color: "text.secondary" }}>
                  <SearchIcon style={{ width: 16, height: 16 }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end" sx={{ color: "text.secondary" }}>
                  <CloseIcon
                    style={{ width: 16, height: 16, cursor: "pointer" }}
                    onClick={() => setSearchQuery("")}
                    role="button"
                    aria-label="Clear search"
                  />
                </InputAdornment>
              ) : null,
            },
          }}
        />

        <ArcStack spacing={1} sx={{ flex: 1, minHeight: 0 }}>
          <ArcStack
            spacing={1}
            sx={{
              flex: 1,
              overflowY: "auto",
              minHeight: 0,
            }}
          >
            {displayOrders.map((order, index) => {
              const isSelected = selectedOrder
                ? (isMissionOrder(selectedOrder) && isMissionOrder(order) && selectedOrder.missionId === order.missionId) ||
                  (isNewOrder(selectedOrder) && isNewOrder(order) && selectedOrder.id === order.id)
                : false;

              return (
                <ArcOrderCard
                  key={order.missionId || order.id || index}
                  order={order}
                  isSelected={isSelected}
                  onClick={() => onSelectOrder(order, filter)}
                  index={index}
                  status={filter}
                  fullWidth={true}
                />
              );
            })}

            {displayOrders.length === 0 && !isLoadingList && (
              <ArcStack alignItems="center" py={4}>
                <ArcText.h6 color="text.secondary">
                  No {OrderStatusNames[filter].toLowerCase()} orders found
                </ArcText.h6>
              </ArcStack>
            )}

            {isLoadingList && (
              <ArcStack alignItems="center" py={4}>
                <ArcText.h6 color="text.secondary">
                  Loading orders...
                </ArcText.h6>
              </ArcStack>
            )}
          </ArcStack>

          {showPagination && (
            <ArcStack alignItems="center" mt={2}>
              <ArcPagination
                page={currentPage + 1}
                count={totalPages}
                onChange={handlePageChange}
              />
            </ArcStack>
          )}
        </ArcStack>
      </ArcStack>
    </ArcBaseSidebar>
  );
};
